import moment from "moment/moment";
import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "../../Hooks/useApi";
import Loading from "../../Shared/Loading";
import UserDashboardDataTable from "../UserDashboard/UserDashboardReports/UserDashboardDataTable";
import AdminDashboardDayGrid from "../AdminDashboard/AdminDoctorPatient/AdminDashboardDayGrid";
import UserDashboardProgressTable from "../UserDashboard/UserDashboardReports/UserDashboardProgressTable";
import { Icon } from "@iconify/react";
import "./PatientActivityReport.css";

const GAME_ID_MAP = {
  smoothMovement: "smooth",
  rapidMovement: "rapid",
  pingPong: "pingPong",
  rainDrop: "rainDrop",
  matchGabor: "matchGabor",
  randomObstacle: "randomObstacle",
  searchQuest: "searchQuest",
  rockChart: "rockChart",
  theShooter: "theShooter",
  colorTrap: "colorTrap",
};

const EMPTY_GAME_DATA = {
  smooth: [], rapid: [], pingPong: [], rainDrop: [],
  matchGabor: [], randomObstacle: [], searchQuest: [],
  rockChart: [], theShooter: [], colorTrap: [],
};

const PatientActivityReport = ({
  userId,
  title = "Exercise Analytics",
  description = "Track game scores, training frequency, and overall recovery progress",
}) => {
  const [date, setDate] = useState(moment().format("YYYY-MM-D"));
  const [gameData, setGameData] = useState(EMPTY_GAME_DATA);
  const [packages, setPackages] = useState(null);

  const { data: packageData, loading: packageLoading } = useQuery(
    userId ? `/package/specific?fieldName=userId&fieldValue=${userId}` : null
  );

  const { data: historyData, loading: historyLoading, error: historyError } = useQuery(
    userId ? `/games/all-history?userId=${userId}` : null
  );

  const loading = packageLoading || historyLoading;

  useEffect(() => {
    if (packageData?.data?.length) {
      setPackages(packageData.data[0]);
    }
  }, [packageData]);

  useEffect(() => {
    if (historyData?.status === "success" && Array.isArray(historyData?.data)) {
      const acc = { ...EMPTY_GAME_DATA };
      // Deep copy each array so we don't mutate the constant
      Object.keys(acc).forEach((k) => { acc[k] = []; });

      historyData.data.forEach((record) => {
        const key = GAME_ID_MAP[record.gameId];
        if (key) acc[key].push(record);
      });

      setGameData(acc); // single state update instead of 10–20 individual ones
    }
  }, [historyData]);

  const allGameData = useMemo(() => [
    ...gameData.smooth,
    ...gameData.rapid,
    ...gameData.pingPong,
    ...gameData.rainDrop,
    ...gameData.colorTrap,
    ...gameData.theShooter,
    ...gameData.rockChart,
    ...gameData.searchQuest,
    ...gameData.randomObstacle,
    ...gameData.matchGabor,
  ], [gameData]);

  const days = useMemo(() => {
    const result = [];
    const start = moment().subtract(52, "weeks").startOf("isoWeek");
    const end = moment().endOf("isoWeek");

    let current = start.clone();
    while (current.isBefore(end) || current.isSame(end, "day")) {
      result.push(current.format("YYYY-MM-D"));
      current.add(1, "day");
    }
    return result;
  }, []);

  const monthLabels = useMemo(() => {
    const labels = [];
    let lastMonth = "";
    for (let col = 0; col < 53; col++) {
      const dayIndex = col * 7;
      if (dayIndex < days.length) {
        const dateObj = moment(days[dayIndex], "YYYY-MM-D");
        const monthName = dateObj.format("MMM");
        if (monthName !== lastMonth) {
          labels.push({ text: monthName, colIndex: col });
          lastMonth = monthName;
        }
      }
    }
    return labels;
  }, [days]);

  const filterSelected = useMemo(
    () => allGameData.filter((o) => o.date === date),
    [allGameData, date]
  );

  if (loading) return <Loading />;

  return (
    <div className="max-w-6xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-indigo-50 rounded-xl">
            <Icon icon="solar:chart-square-bold" className="w-6 h-6 text-secondary" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-800 tracking-tight">{title}</h1>
            <p className="text-xs text-slate-400 font-medium mt-0.5">{description}</p>
          </div>
        </div>

        <div>
          {packages?.expireDate ? (
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-indigo-100 bg-indigo-50/50 text-secondary text-xs font-bold">
              <Icon icon="solar:calendar-date-bold" className="w-4 h-4 text-primary" />
              <span>Plan Expires: {packages.expireDate}</span>
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-rose-100 bg-rose-50/50 text-rose-700 text-xs font-bold">
              <Icon icon="solar:danger-bold" className="w-4 h-4 text-rose-500" />
              <span>No Active Package</span>
            </div>
          )}
        </div>
      </div>

      {/* Heatmap */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
          <div>
            <h2 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Exercise Frequency</h2>
            <p className="text-[10px] text-slate-400 font-medium mt-0.5">Logs over the past 365 days — click a cell to filter by date</p>
          </div>
          <div className="flex items-center gap-4 text-xs font-bold text-slate-400">
            <span>Selected: <strong className="text-indigo-600 font-extrabold">{date}</strong></span>
            <div className="flex items-center gap-1.5 text-[10px]">
              <span>Less</span>
              <span className="w-2.5 h-2.5 bg-slate-100 rounded-sm" />
              <span className="w-2.5 h-2.5 bg-indigo-300 rounded-sm" />
              <span className="w-2.5 h-2.5 bg-[#291D89] rounded-sm" />
              <span>More</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 py-2">
          <div className="grid grid-rows-7 font-bold text-[10px] text-slate-400 pr-2 py-0.5 h-[138px]">
            <span className="row-start-1 flex items-center">M</span>
            <span className="row-start-3 flex items-center">W</span>
            <span className="row-start-5 flex items-center">F</span>
          </div>
          <div className="flex-1 min-w-[640px]">
            <div className="grid grid-cols-[repeat(53,_1fr)] mb-2 font-bold text-[10px] text-slate-400 tracking-wider">
              {monthLabels.map((lbl, idx) => (
                <span
                  key={idx}
                  style={{ gridColumnStart: lbl.colIndex + 1 }}
                >
                  {lbl.text}
                </span>
              ))}
            </div>
            <div className="days gap-[2px]">
              {days.map((day, i) => (
                <AdminDashboardDayGrid
                  key={i}
                  setDate={setDate}
                  date={date}
                  allGameData={allGameData}
                  day={day}
                  colIndex={Math.floor(i / 7)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-4">
          <h2 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
            <Icon icon="solar:medal-star-bold" className="w-4 h-4 text-cyan-500" />
            Exercise Scoreboard ({date})
          </h2>
          <div className="overflow-x-auto border border-slate-100 rounded-xl">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/75 border-b border-slate-100">
                  <th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Exercise Name</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Date Logged</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-right">Game Score</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filterSelected.length > 0 ? (
                  filterSelected.map((game, idx) => (
                    <UserDashboardDataTable key={idx} game={game} />
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="text-center py-10 text-xs font-semibold text-slate-400">
                      No game records found for {date}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-4">
          <h2 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
            <Icon icon="solar:ranking-bold" className="w-4 h-4 text-indigo-500" />
            Performance Progress ({date})
          </h2>
          <div className="overflow-x-auto border border-slate-100 rounded-xl">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/75 border-b border-slate-100">
                  <th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Exercise Name</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Date Logged</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-right">Progress Score</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filterSelected.length > 0 ? (
                  filterSelected.map((game, idx) => (
                    <UserDashboardProgressTable key={idx} game={game} />
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="text-center py-10 text-xs font-semibold text-slate-400">
                      No progress records found for {date}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientActivityReport;
